/**
 * AuthContext - Supabase OAuth Authentication
 *
 * Manages auth state with Google + GitHub OAuth.
 * Follows project patterns: HMR-safe globalThis key, custom hook with error boundary.
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { buildAuthRedirectUrl } from './authRedirect';
import { supabase } from '../services/supabase/client';
import { navigateToLanding, runSessionExitGuard } from '../routes/appRouter';
import { showAppToast } from '../services/toastService';

// HMR-resilient context
const CONTEXT_KEY = '__GLOWPOP_AUTH_CONTEXT__';
if (!globalThis[CONTEXT_KEY]) {
  globalThis[CONTEXT_KEY] = createContext(null);
}
const AuthContext = globalThis[CONTEXT_KEY];

// Mock user for local dev when Supabase is not configured
const DEV_USER = !supabase ? { id: 'dev-local', email: 'dev@localhost' } : null;

export const AuthProvider = ({ children }) => {
  // When supabase is not configured, skip auth entirely with a mock user
  const [user, setUser] = useState(DEV_USER);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(!!supabase);

  // Initialize: check existing session + listen for changes
  useEffect(() => {
    if (!supabase) return;

    // Check current session
    supabase.auth.getSession()
      .then(({ data: { session: currentSession } }) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setIsLoading(false);
      })
      .catch((error) => {
        console.warn('[auth] getSession failed:', error?.message || error);
        setIsLoading(false);
      });

    // Listen for auth changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setIsLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) return;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: buildAuthRedirectUrl() }
    });
    if (error) console.error('Google sign-in error:', error.message);
  }, []);

  const signInWithGitHub = useCallback(async () => {
    if (!supabase) return;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: { redirectTo: buildAuthRedirectUrl() }
    });
    if (error) console.error('GitHub sign-in error:', error.message);
  }, []);

  /**
   * Signing out unmounts the open session, so it leaves through the same exit as every other way
   * out and is refused (false) while that session has work running or changes unsaved. `force`
   * is for expired auth only: that session can no longer save, so there is nothing to wait for.
   *
   * An approved exit closes the session's transition queue; the route owner then unmounts the
   * workspace on the landing route (its own guard call meets the closed queue and approves at
   * once), and only then is the revoke awaited. Visible auth has one writer, the
   * `onAuthStateChange` listener: a failed revoke leaves the user signed in, on the landing page.
   */
  const signOut = useCallback(async (options, { force = false } = {}) => {
    if (!supabase) return false;
    const exit = force ? null : await runSessionExitGuard();
    if (exit && !exit.allowed) return false;
    if (exit) navigateToLanding();
    let error = null;
    try {
      ({ error } = await supabase.auth.signOut(options));
    } catch (err) {
      error = err;
    }
    if (!error) return true;
    console.error('Sign-out error:', error.message);
    showAppToast('Sign-out failed. You are still signed in.', 'error');
    return false;
  }, []);

  /**
   * Get current access token (for passing to FAL proxy)
   * @returns {string|null}
   */
  const getAccessToken = useCallback(() => {
    return session?.access_token ?? null;
  }, [session]);

  // Who is signed in as of the latest commit, read at call time: a child unmounted by a sign-out
  // never renders with the signed-out user, but its effect cleanup (which runs after this layout
  // effect) can still ask.
  const signedInUserIdRef = useRef(user?.id ?? null);
  useLayoutEffect(() => {
    signedInUserIdRef.current = user?.id ?? null;
  }, [user]);
  const getSignedInUserId = useCallback(() => signedInUserIdRef.current, []);

  const value = {
    user,
    session,
    isLoading,
    signInWithGoogle,
    signInWithGitHub,
    signOut,
    getAccessToken,
    getSignedInUserId
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
